import './dateMenu.css'

import type { Range } from 'api/System/types'
import classNames from 'classnames'
import { CommonButton } from 'CommonButton'
import { TypeAnalysis } from 'DatePickerDialog/constants'
import { moment } from 'locale'
import React, { PureComponent } from 'react'
import { withTranslation } from 'react-i18next'
import { throttle } from 'throttle-debounce'
import type { Translate } from 'types'
import { getMomentDate, momentValidDateFormat } from 'utils'

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'

interface Props {
  changeDate: ({ dateCurrent }: { dateCurrent: string }) => void
  dateCurrent: string
  datePickerRange: Range
  isFetching: boolean
  ranges: Range[]
  todayWarning: boolean
  t: Translate
  toggleDatePickerDialog: () => void
  typeAnalysis: TypeAnalysis
}

interface State {
  isSticky: boolean
  showDatePicker: boolean
}

interface DateMenuRef {
  offsetTop: number
}

class DateMenu extends PureComponent<Props, State> {
  state = {
    isSticky: false,
    showDatePicker: false,
  }

  componentDidMount() {
    this.scrollThrottle = throttle(1000 / 60, this.onScroll)
    window.addEventListener('scroll', this.scrollThrottle)
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.scrollThrottle)
    this.scrollThrottle.cancel()
  }

  dateMenu = React.createRef<DateMenuRef>()

  scrollThrottle: throttle<any> | null = null

  onScroll = () => {
    if (!this.dateMenu.current) {
      return
    }

    const shouldBeSticky = window.pageYOffset > this.dateMenu.current.offsetTop
    if (shouldBeSticky && !this.state.isSticky) {
      this.setState({ isSticky: true })
    } else if (!shouldBeSticky && this.state.isSticky) {
      this.setState({ isSticky: false })
    }
  }

  showModal = () => {
    this.setState({ showDatePicker: true })
  }

  hideModal = () => {
    this.setState({ showDatePicker: false })
  }

  pickTodayDate = () => {
    const { changeDate, isFetching } = this.props

    if (!isFetching && !this.isToday) {
      changeDate({ dateCurrent: moment().format(momentValidDateFormat) })
    }
  }

  pickYesterdayDate = () => {
    const { changeDate, isFetching } = this.props

    if (!isFetching && !this.isYesterday) {
      changeDate({ dateCurrent: moment().subtract('1', 'day').format(momentValidDateFormat) })
    }
  }

  get selectedRangeText() {
    const { dateCurrent, datePickerRange, typeAnalysis } = this.props
    const endDate = Math.min(
      moment(datePickerRange?.end).valueOf(),
      typeAnalysis === TypeAnalysis.perEndDate ? moment(dateCurrent).valueOf() : Date.now(),
    )

    if (!datePickerRange) {
      return getMomentDate(dateCurrent).format('L')
    }

    return `${getMomentDate(datePickerRange.start).format('L')} - ${getMomentDate(endDate).format('L')}`
  }

  get isToday() {
    const { dateCurrent, typeAnalysis } = this.props

    return (
      moment(dateCurrent).format(momentValidDateFormat) === moment().format(momentValidDateFormat) &&
      typeAnalysis === TypeAnalysis.perDay
    )
  }

  get isYesterday() {
    const { dateCurrent, typeAnalysis } = this.props

    return (
      moment(dateCurrent).format(momentValidDateFormat) === moment().subtract('1', 'day').format(momentValidDateFormat) &&
      typeAnalysis === TypeAnalysis.perDay
    )
  }

  get isCustom() {
    return (!this.isToday && !this.isYesterday) || false
  }

  render() {
    const { isCustom } = this
    const { isFetching, t: translate, todayWarning } = this.props

    return (
      <Box className="date-menu-wrapper">
        <Box {...{ ref: this.dateMenu as any }} className={classNames('date-menu', { sticky: this.state.isSticky })}>
          <Grid container spacing="0.8rem" pl="1.5rem" pr="1.5rem">
            <Grid item xs={3}>
              <CommonButton
                aria-label={translate('menu.date.today')}
                className="common-button"
                disabled={isFetching && this.isYesterday}
                isUpperCase={false}
                variant={this.isToday ? 'primary' : 'secondary'}
                onClick={this.pickTodayDate}
              >
                {translate('menu.date.today')}
                {todayWarning && ` | ${translate('menu.date.todayWarning')}`}
              </CommonButton>
            </Grid>
            <Grid item xs={3}>
              <CommonButton
                aria-label={translate('menu.date.yesterday')}
                className="common-button"
                disabled={isFetching && this.isToday}
                isUpperCase={false}
                variant={this.isYesterday ? 'primary' : 'secondary'}
                onClick={this.pickYesterdayDate}
              >
                {translate('menu.date.yesterday')}
              </CommonButton>
            </Grid>
            <Grid item xs={6}>
              <CommonButton
                aria-label={translate('common.date')}
                className="common-button"
                isUpperCase={false}
                onClick={this.props.toggleDatePickerDialog}
                variant={isCustom ? 'primary' : 'secondary'}
              >
                <Box className="customDate">
                  {isCustom && <span className="range">{this.selectedRangeText}</span>}
                  {!isCustom && <span className="day">{translate('common.date')}</span>}
                  <i className="fm fm-calendar" />
                </Box>
              </CommonButton>
            </Grid>
          </Grid>
        </Box>
      </Box>
    )
  }
}

export default withTranslation()(DateMenu) as any
